import React, { Fragment, useContext, useMemo } from 'react';
import Div from '@/shared/Div';
import date from '@/utils/date';
import Paragraph from '@/shared/Paragraph';
import classNames from 'classnames';
import Header from '@/shared/Header';
import { MapType } from '@/types';
import { AMC_NAME, AMC_PLAN_TYPES, AMC_PLAN_TYPE_NAMES, PLAN_BORDER_MAP } from '@/constants/amc';
import { DEVICE_STATUS } from '@/constants/app';
import { useAppConfigContext } from '@/context/AppContext';
import { getDiscountTagText } from '../AMC/PlanCard';
import { getFormattedPrice } from '../MultiYearPlanPricing/utils';
import SparkleIcon from '../Icons/Sparkle';
import is from '@/utils/is';

const warrentyLabel = 'Active Warranty';

type DeviceStatusProps = {
  planType: string;
  currentPlan: {
    startDate: string;
    endDate: string;
  };
  expiryStatus: {
    daysToExpire: number;
    status: keyof typeof DEVICE_STATUS;
    isAboutToExpire: string;
  };
  isRental: boolean | undefined;
  isOpenServiceRequest?: boolean;
  numberOfCtas?: number;
  savingPromotionData?: any;
  upcomingAMCPlan?: any;
};

type DeviceStatusCompPropTypes = {
  planDuration: number;
  endDate: string;
  planType: keyof typeof AMC_PLAN_TYPES;
  isOpenServiceRequest?: boolean;
  numberOfCtas?: number;
  savingData?: any;
  promoData?: any;
  cohortBasedSavings?: string;
  discountTagText?: string;
  showBottomBorder?: boolean;
};

const IW = ({
  planDuration,
  endDate,
  isOpenServiceRequest,
  numberOfCtas,
  savingData,
  promoData,
  cohortBasedSavings = '',
  discountTagText,
  showBottomBorder,
}: DeviceStatusCompPropTypes) => {
  const costSaved = savingData?.costSaved;
  return (
    <Div
      className={classNames(
        'flex flex-col py-4 px-6 gap-s4 border-y  border-dashed border-neutral-100',
        {
          ' border-b-0': isOpenServiceRequest && numberOfCtas === 0 && !showBottomBorder,
        },
      )}
    >
      {promoData && is.not.empty.object(promoData) && promoData !== null ? (
        <Div className="px-s3 py-s2 border  rounded-cxs bg-green-500 w-fit text-cti-on-primary text-body-xs">
          {discountTagText}
        </Div>
      ) : null}
      <Div className="flex flex-col">
        <Header size="sm" className="text-black/70">
          {warrentyLabel}
        </Header>
        <Paragraph className="text-black/50">
          {`Valid till ${date
            .format(endDate, {
              month: 'short',
              day: '2-digit',
            })
            .replaceAll('-', ' ')}`}
        </Paragraph>
      </Div>
      {savingData &&
      is.not.empty.object(savingData) &&
      savingData !== null &&
      costSaved &&
      costSaved !== null ? (
        <Div className="p-s3 flex gap-s3 border rounded-cxs border-green-300 bg-green-100 w-fit justify-center items-center">
          <SparkleIcon />
          <p
            className="text-green-500 text-body-sm"
            dangerouslySetInnerHTML={{ __html: cohortBasedSavings }}
          />
        </Div>
      ) : null}
    </Div>
  );
};

const IC = ({
  planDuration,
  endDate,
  planType,
  isOpenServiceRequest,
  numberOfCtas,
  savingData,
  promoData,
  cohortBasedSavings = '',
  discountTagText,
}: DeviceStatusCompPropTypes) => {
  const costSaved = savingData?.costSaved;
  return (
    <Div
      className={classNames('flex flex-col py-4 gap-s4 px-6 border-y', {
        [PLAN_BORDER_MAP[planType]]: true,
      })}
    >
      {promoData && is.not.empty.object(promoData) && promoData !== null ? (
        <Div className="px-s3 py-s2 border  rounded-cxs bg-green-500 w-fit text-cti-on-primary text-body-xs">
          {discountTagText}
        </Div>
      ) : null}
      <Div className="flex flex-col">
        <Header size="sm" className={classNames('text-black/70')}>
          {AMC_NAME} {AMC_PLAN_TYPE_NAMES[planType?.toUpperCase()]}
        </Header>
        <Paragraph className={classNames('text-black/50')}>
          {`Valid till ${date
            .format(endDate, {
              month: 'short',
              day: '2-digit',
            })
            .replaceAll('-', ' ')}`}
        </Paragraph>
      </Div>
      {savingData &&
      is.not.empty.object(savingData) &&
      savingData !== null &&
      costSaved &&
      costSaved !== null ? (
        <Div className="p-s3 flex gap-s3 border rounded-cxs border-green-300 bg-green-100 w-fit justify-center items-center">
          <SparkleIcon />
          <p
            className="text-green-500 text-body-sm"
            dangerouslySetInnerHTML={{ __html: cohortBasedSavings }}
          />
        </Div>
      ) : null}
    </Div>
  );
};

const OC = ({
  planDuration,
  endDate,
  isOpenServiceRequest,
  planType,
  numberOfCtas,
  savingData,
  promoData,
  cohortBasedSavings = '',
  discountTagText,
}: DeviceStatusCompPropTypes) => {
  const costSaved = savingData?.costSaved;
  return (
    <Div
      className={classNames(
        'flex flex-col py-4 px-6 border-y gap-s4  border-dashed border-neutral-100',
        {
          ' border-b-0': isOpenServiceRequest && numberOfCtas === 0,
        },
      )}
    >
      {promoData && is.not.empty.object(promoData) && promoData !== null ? (
        <Div className="px-s3 py-s2 border  rounded-cxs bg-green-500 w-fit text-cti-on-primary text-body-xs">
          {discountTagText}
        </Div>
      ) : null}
      <Div className="flex flex-col">
        <Header size="sm" className="text-black/70">
          {AMC_NAME} {AMC_PLAN_TYPE_NAMES[planType?.toUpperCase()]}
        </Header>
        <Paragraph className=" text-cu-warning">
          {`Expired on ${date
            .format(endDate, {
              month: 'short',
              day: '2-digit',
            })
            .replaceAll('-', ' ')}`}
        </Paragraph>
      </Div>
      {savingData &&
      is.not.empty.object(savingData) &&
      savingData !== null &&
      costSaved &&
      costSaved !== null ? (
        <Div className="p-s3 flex gap-s3 border rounded-cxs border-green-300 bg-green-100 w-fit justify-center items-center">
          <SparkleIcon />
          <p
            className="text-green-500 text-body-sm"
            dangerouslySetInnerHTML={{ __html: cohortBasedSavings }}
          />
        </Div>
      ) : null}
    </Div>
  );
};

const OW = ({
  planDuration,
  endDate,
  isOpenServiceRequest,
  numberOfCtas,
  savingData,
  promoData,
  cohortBasedSavings = '',
  discountTagText,
  showBottomBorder,
}: DeviceStatusCompPropTypes) => {
  const costSaved = savingData?.costSaved;
  return (
    <Div
      className={classNames(
        'flex flex-col py-4 px-6 border-y gap-s4 border-dashed border-neutral-100',
        {
          ' border-b-0': isOpenServiceRequest && numberOfCtas === 0 && !showBottomBorder,
        },
      )}
    >
      {promoData && is.not.empty.object(promoData) && promoData !== null ? (
        <Div className="px-s3 py-s2 border  rounded-cxs bg-green-500 w-fit text-cti-on-primary text-body-xs">
          {discountTagText}
        </Div>
      ) : null}
      <Div className="flex flex-col">
        <Header size="sm" className="text-black/70">
          {warrentyLabel}
        </Header>
        <Paragraph className=" text-cu-negative">
          {`Expired on ${date
            .format(endDate, {
              month: 'short',
              day: '2-digit',
            })
            .replaceAll('-', ' ')}`}
        </Paragraph>
      </Div>

      {savingData &&
      is.not.empty.object(savingData) &&
      savingData !== null &&
      costSaved &&
      costSaved !== null ? (
        <Div className="p-s3 flex gap-s3 border rounded-cxs border-green-300 bg-green-100 w-fit justify-center items-center">
          <SparkleIcon />
          <p
            className="text-green-500 text-body-sm"
            dangerouslySetInnerHTML={{ __html: cohortBasedSavings }}
          />
        </Div>
      ) : null}
    </Div>
  );
};

const RENTAL = ({
  overdue = false,
  endDate,
  isOpenServiceRequest,
  numberOfCtas,
}: {
  overdue?: boolean;
  endDate?: any;
  overdueDays?: number;
  isOpenServiceRequest?: boolean;
  numberOfCtas?: number;
}) => {
  return (
    <Div
      className={classNames('flex flex-col py-4 px-6 border-y border-dashed border-neutral-100', {
        ' border-b-0': isOpenServiceRequest && numberOfCtas === 0,
      })}
    >
      <Header size="sm" className="text-black/70">
        Rented Device
      </Header>
      <Paragraph className={`text-black/50 ${overdue ? 'text-cu-warning' : ''}`}>
        {overdue ? 'Payment overdue since ' : 'Active till '}
        {date.format(endDate, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </Paragraph>
    </Div>
  );
};

const STATUS_COMP_MAP: MapType = {
  IW,
  IC,
  OC,
  OW,
};

function DeviceStatus({
  planType,
  currentPlan,
  expiryStatus,
  isRental,
  isOpenServiceRequest,
  numberOfCtas,
  savingPromotionData = {},
  upcomingAMCPlan,
}: DeviceStatusProps) {
  const { status, daysToExpire } = expiryStatus;

  const { startDate, endDate } = currentPlan;
  const {
    state: {
      config: { cohortRule },
    },
  } = useAppConfigContext();
  const { savingData, promoData } = savingPromotionData;
  const { savingsText, discountLabels } = cohortRule;
  const {
    startDate: startingDate,
    endDate: endingDate,
    agreementId,
    planDuration: planLength,
  } = upcomingAMCPlan;

  const showBottomBorder = useMemo(() => {
    if (
      is.empty.string(agreementId) ||
      is.empty.string(planLength) ||
      is.empty.string(startingDate) ||
      is.empty.string(endingDate)
    )
      return false;
    return true;
  }, [startingDate, endingDate, agreementId, planLength]);

  const attributes = useMemo(() => {
    const discountItem = promoData?.data?.promotions?.find((item: any) => item.type === 'DISCOUNT');
    return discountItem ? discountItem.attributes : null;
  }, [promoData]);

  const discountTagText = useMemo(
    () =>
      getDiscountTagText(
        discountLabels ?? {},
        attributes?.discount?.percentage,
        daysToExpire ?? 0,
        status,
      ),
    [attributes, discountLabels, daysToExpire, status],
  );
  const overdue = daysToExpire < 0;
  const cohortBasedSavings = useMemo(() => {
    let savings = savingsText[status];
    savings = (savings ?? '').replaceAll(
      '{{VALUE}}',
      `<b> ${getFormattedPrice(Math.floor(savingData?.costSaved))}</b>`,
    );
    return savings;
  }, [savingsText, savingData, status]);

  const planDuration = useMemo(() => date.getPlanInYears(startDate, endDate), [startDate, endDate]);
  const Comp = STATUS_COMP_MAP[status] ?? Fragment;

  if (isRental) return RENTAL({ overdue, endDate, numberOfCtas, isOpenServiceRequest });

  return (
    <Comp
      planDuration={planDuration}
      planType={planType}
      endDate={endDate}
      isOpenServiceRequest={isOpenServiceRequest}
      numberOfCtas={numberOfCtas}
      savingData={savingData}
      promoData={promoData}
      cohortBasedSavings={cohortBasedSavings}
      discountTagText={discountTagText}
      showBottomBorder={showBottomBorder}
    />
  );
}

export default DeviceStatus;
